import {
  runBulk,
  type BulkAction,
} from "@/components/molecules/tables/bulk-actions";
import { useHasRole } from "@/features/clients/hooks/use-has-role";
import { useDeleteFaq, useUpdateFaq } from "@/features/faq/hooks/use-faq";
import type { Faq } from "@/features/faq/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArchiveIcon,
  ArrowsClockwiseIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useMemo } from "react";

export const useFaqBulkActions = (): BulkAction<Faq>[] => {
  const { t } = useTranslation("faq");
  const isOwner = useHasRole("OWNER");
  const deleteFaq = useDeleteFaq();
  const updateFaq = useUpdateFaq();

  return useMemo(() => {
    if (!isOwner) return [];

    // The list is filtered by status, but a selection can still mix both when
    // the filter changes — so each action only touches the rows it applies to.
    const active = (rows: Faq[]) => rows.filter((f) => f.status !== "archived");
    const archived = (rows: Faq[]) =>
      rows.filter((f) => f.status === "archived");

    return [
      {
        key: "archive",
        label: t("actions.archive"),
        icon: <ArchiveIcon className="h-4 w-4" />,
        isVisible: (rows) => active(rows).length > 0,
        onAction: async (rows) => {
          await runBulk(
            active(rows),
            (faq) =>
              updateFaq.mutateAsync({
                id: faq.id,
                data: { status: "archived" },
              }),
            {
              success: (count) => t("messages.bulkArchiveSuccess", { count }),
              error: (count) => t("messages.bulkArchiveError", { count }),
            },
          );
        },
      },
      {
        key: "unarchive",
        label: t("actions.unarchive"),
        icon: <ArrowsClockwiseIcon className="h-4 w-4" />,
        isVisible: (rows) => archived(rows).length > 0,
        onAction: async (rows) => {
          await runBulk(
            archived(rows),
            (faq) =>
              updateFaq.mutateAsync({ id: faq.id, data: { status: "active" } }),
            {
              success: (count) => t("messages.bulkUnarchiveSuccess", { count }),
              error: (count) => t("messages.bulkUnarchiveError", { count }),
            },
          );
        },
      },
      {
        key: "delete",
        label: t("actions.deleteSelected"),
        icon: <TrashIcon className="h-4 w-4" />,
        variant: "destructive",
        confirm: (rows) =>
          t("messages.bulkDeleteConfirm", { count: rows.length }),
        onAction: async (rows) => {
          await runBulk(rows, (faq) => deleteFaq.mutateAsync(faq.id), {
            success: (count) => t("messages.bulkDeleteSuccess", { count }),
            error: (count) => t("messages.bulkDeleteError", { count }),
          });
        },
      },
    ];
  }, [isOwner, t, deleteFaq, updateFaq]);
};
